<template>
  <component
    :is="tag"
    class="play-btn"
    :class="[`play-btn--${variant}`, `play-btn--${size}`, { 'play-btn--block': block }]"
    :type="tag === 'button' ? type : null"
    :disabled="tag === 'button' ? disabled : null"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <span
      v-if="$slots.icon"
      class="play-btn__icon"
    ><slot name="icon" /></span>
    <span class="play-btn__label"><slot /></span>
  </component>
</template>

<script>
/**
 * PlayButton — pattern reference for the Play system component layer.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors, so a
 * palette swap requires zero changes here. Replaces bootstrap-vue's
 * <b-button> in the rebrand.
 *
 * Renders as <button> by default, or <a>/<router-link> via `tag` so it
 * works for both actions and links. All native attrs/listeners pass through.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` in the template is the only Vue 2-ism
 * here. In Vue 3 $listeners is removed (merged into $attrs); drop that line
 * and v-bind="$attrs" keeps forwarding listeners. The props/slots contract is
 * already Vue 3-safe.
 */
export default {
  name: 'PlayButton',
  inheritAttrs: false,
  props: {
    /** Visual role: primary CTA, lime highlight, violet, outlined ghost, or
     *  borderless plain (quiet icon/utility control — inherits its color) */
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'highlight', 'secondary', 'ghost', 'plain'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    /** Element to render: 'button' | 'a' | 'router-link' */
    tag: {
      type: String,
      default: 'button',
    },
    type: {
      type: String,
      default: 'button',
    },
    block: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.play-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--font-display);
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
  transition:
    transform var(--dur) var(--ease-bounce),
    box-shadow var(--dur) var(--ease-bounce);
  will-change: transform;
  -webkit-tap-highlight-color: transparent;

  &__label { display: inline-block; }
  &__icon {
    display: inline-flex;
    align-items: center;
    font-size: 1.1em;
  }

  /* ── Sizes ── */
  &--sm { padding: 9px 16px;  font-size: 13.5px; border-radius: var(--radius-sm); }
  &--md { padding: 13px 24px; font-size: 15px; }
  &--lg { padding: 16px 30px; font-size: 17px;  border-radius: var(--radius-lg); }

  &--block { display: flex; width: 100%; }

  /* ── Press feel (shared) ── */
  &:active {
    transform: translateY(3px);
    box-shadow: var(--shadow-press);
  }

  &:disabled,
  &.disabled {
    opacity: .55;
    pointer-events: none;
  }

  /* ── Variants ──
     Hard offset shadow uses each role's -strong slot; a soft colored
     glow adds depth. Both grow on hover. */
  &--primary {
    background: var(--c-primary);
    color: var(--c-primary-on);
    box-shadow: 0 6px 0 var(--c-primary-strong), 0 16px 28px -12px var(--c-primary);
    &:hover { box-shadow: 0 9px 0 var(--c-primary-strong), 0 22px 32px -12px var(--c-primary); }
    &:active { box-shadow: 0 2px 0 var(--c-primary-strong); }
  }

  &--highlight {
    background: var(--c-highlight);
    color: var(--c-highlight-on);
    box-shadow: 0 6px 0 var(--c-highlight-strong), 0 16px 28px -12px var(--c-highlight-strong);
    &:hover { box-shadow: 0 9px 0 var(--c-highlight-strong), 0 22px 32px -12px var(--c-highlight-strong); }
    &:active { box-shadow: 0 2px 0 var(--c-highlight-strong); }
  }

  &--secondary {
    background: var(--c-secondary);
    color: var(--c-secondary-on);
    box-shadow: 0 6px 0 var(--c-secondary-strong), 0 16px 28px -12px var(--c-secondary);
    &:hover { box-shadow: 0 9px 0 var(--c-secondary-strong), 0 22px 32px -12px var(--c-secondary); }
    &:active { box-shadow: 0 2px 0 var(--c-secondary-strong); }
  }

  &--ghost {
    background: var(--surface-card);
    color: var(--text-strong);
    border: var(--border-w) solid var(--border-ink);
    box-shadow: var(--shadow-hard-sm);
    &:hover { box-shadow: var(--shadow-hard); transform: translateY(-1px); }
    &:active { box-shadow: var(--shadow-press); transform: translateY(3px); }
  }

  /* Quiet utility control (locale switcher, toolbar toggles): no box, no
     printed shadow. Role-neutral — color comes from the parent via `inherit`,
     so it can pair with a sibling icon button (e.g. the theme toggle). */
  &--plain {
    background: transparent;
    color: inherit;
    &:hover { opacity: .7; }
    &:active { transform: none; box-shadow: none; }
  }
}
</style>
